import { css } from "@emotion/core";
import styled from "@emotion/styled";
import { Link as RouterLink } from "react-router-dom";

import ApplicantsCheckList from "shared/components/ApplicantsCheckList";
import FlexContainer from "shared/components/FlexContainer";
import { ActionLink } from "shared/components/Links";
import BaseProgressBar from "shared/components/ProgressBar";
import { RegularText, SmallText } from "shared/components/Typography";
import { COLORS, MEDIA_QUERIES, colors } from "shared/config/constants";

const ProgressBar = styled(BaseProgressBar)`
  margin: 16px 0 12px;
  width: 200px;
`;

const NotAvailableSpan = styled.span`
  font-weight: 300;
  color: ${COLORS.secondary};
`;

const PrimaryText = styled(RegularText)`
  & ul li {
    margin-left: 0;
    padding-left: 0;
  }
`;

const SecondaryText = styled(SmallText)`
  color: ${COLORS.secondary};
`;

const FlexWrapper = styled(FlexContainer)`
  & > * {
    margin-right: 5px;
  }
`;

const Cell = styled(FlexContainer)`
  flex-direction: column;
  justify-content: center;
  min-height: 70px;
  ${MEDIA_QUERIES.w.ltSm} {
    min-height: auto;
  }
`;

const StyledCheckList = styled(ApplicantsCheckList, {
  shouldForwardProp: (prop) => prop !== "$listRedesign",
})`
  color: ${COLORS.primary};

  ${({ $listRedesign }) =>
    $listRedesign &&
    css`
      li {
        align-items: center;
        word-break: normal;
        overflow-wrap: normal;
        min-width: 0;
      }
    `}
`;

const Link = styled(ActionLink)`
  min-height: 40px;
  align-items: center;
  color: ${colors.secondary.base};
`;

const LinkText = styled(FlexContainer)`
  min-height: 40px;
  align-items: center;
  color: ${colors.secondary.base};
  text-transform: uppercase;
  font-weight: 500;
`;

const RouterLinkText = styled(RouterLink)`
  display: flex;
  min-height: 40px;
  align-items: center;
  color: ${colors.secondary.base};
  text-transform: uppercase;
  font-weight: 500;
  &:hover,
  &:focus {
    color: ${colors.secondary.base};
  }
`;

const DealCellLink = styled(RouterLink)`
  display: block;
  color: inherit;
  text-decoration: none;
  &:hover,
  &:focus {
    color: inherit;
    text-decoration: none;
  }
`;

const ROW = ".ant-table-tbody > tr.ant-table-row";

const RowLinkTableFrame = styled.div`
  ${MEDIA_QUERIES.w.gteMd} {
    .ant-table-thead > tr > th,
    ${ROW} > td {
      padding-left: 16px;
      padding-right: 16px;
    }
  }

  ${ROW} {
    position: relative;
  }

  ${ROW}:hover {
    box-shadow: ${COLORS.boxShadow}, ${COLORS.boxShadowDeep};
    transform: translateZ(0);
    z-index: 2;
  }

  ${ROW} .deals-row-link::after {
    content: "";
    position: absolute;
    inset: 0;
    z-index: 1;
  }

  ${ROW} .deals-row-link:focus-visible {
    outline: none;
  }

  ${ROW} .deals-row-link:focus-visible::after {
    outline: 2px solid ${COLORS.accentDark};
    outline-offset: -2px;
  }

  ${ROW} .integration-mark,
  ${ROW} [data-testid="sentilink-applicant-chip"],
  ${ROW} [data-testid$="-review-id-fraud"] {
    position: relative;
    z-index: 2;
  }
`;

const SENTILINK_RISK_STATUS_VARIANTS = {
  highRisk: {
    color: COLORS.white,
    background: COLORS.lightRed2,
  },
  error: {
    color: COLORS.failedRed,
    background: COLORS.well?.backgroundLightError || "#FDECEC",
  },
  needsReview: {
    color: COLORS.midOrange,
    background: "#FFF4E5",
  },
  lowRisk: {
    color: COLORS.accentDark,
    background: COLORS.lightGreen,
  },
  checking: {
    color: COLORS.textColorSecondary,
    background: COLORS.lightGray1,
  },
  neutral: {
    color: COLORS.textColorSecondary,
    background: COLORS.lightGray2,
  },
};

const SENTILINK_APPLICANT_SHIELD_VARIANTS = {
  highRisk: {
    color: COLORS.lightRed2,
  },
  error: {
    color: COLORS.failedRed,
  },
  needsReview: {
    color: COLORS.midOrange,
  },
  lowRiskMuted: {
    color: COLORS.textColorSecondary,
  },
  checking: {
    color: COLORS.textColorSecondary,
  },
  neutral: {
    color: COLORS.textColorSecondary,
  },
};

const SentilinkApplicantChipContainer = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-left: 6px;
  vertical-align: middle;
`;

const SentilinkApplicantShieldIcon = styled.span`
  display: inline-flex;
  align-items: center;
  line-height: 0;

  svg {
    width: 14px;
    height: 14px;
    fill: ${({ variant }) =>
      SENTILINK_APPLICANT_SHIELD_VARIANTS[variant]?.color ||
      SENTILINK_APPLICANT_SHIELD_VARIANTS.neutral.color};
  }
`;

const SentilinkApplicantChipLabel = styled.span`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  line-height: 1.2;
  white-space: nowrap;
  color: ${({ variant }) =>
    SENTILINK_RISK_STATUS_VARIANTS[variant]?.color ||
    SENTILINK_RISK_STATUS_VARIANTS.neutral.color};
  background-color: ${({ variant }) =>
    SENTILINK_RISK_STATUS_VARIANTS[variant]?.background ||
    SENTILINK_RISK_STATUS_VARIANTS.neutral.background};
`;

export {
  ProgressBar,
  NotAvailableSpan,
  PrimaryText,
  SecondaryText,
  FlexWrapper,
  Cell,
  StyledCheckList,
  Link,
  LinkText,
  RouterLinkText,
  DealCellLink,
  RowLinkTableFrame,
  SentilinkApplicantChipContainer,
  SentilinkApplicantShieldIcon,
  SentilinkApplicantChipLabel,
};
